import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GiHamburgerMenu } from 'react-icons/gi';
import { IoClose } from 'react-icons/io5';
import ZemedicLogo from './ZemedicLogo';
import { useAuth } from '../contexts/AuthContext';

const Navbar = () => {
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showSolutionsDropdown, setShowSolutionsDropdown] = useState(false);
  
  const toggleSolutionsDropdown = () => {
    setShowSolutionsDropdown(!showSolutionsDropdown);
  };
  
  const closeMobileMenu = () => {
    setIsOpen(false);
  };

  return (
    <nav className="bg-black shadow-md border-b border-gray-800">
      <div className="container-custom py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <ZemedicLogo className="h-10 w-auto" fill="#ffffff" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Hover handlers live on the WRAPPER, not the trigger link.
                With them on the link, moving the cursor down into the menu left
                the link first, which unmounted the menu before the pointer
                arrived — the dropdown was unusable by mouse. The wrapper spans
                both, so travelling between them never fires mouseleave. */}
            <div
              className="relative group"
              onMouseEnter={() => setShowSolutionsDropdown(true)}
              onMouseLeave={() => setShowSolutionsDropdown(false)}
            >
              <Link
                to="/solutions"
                className="text-gray-300 hover:text-purple-400 font-medium transition-colors flex items-center"
                onFocus={() => setShowSolutionsDropdown(true)}
                aria-haspopup="true"
                aria-expanded={showSolutionsDropdown}
              >
                Solutions
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </Link>
              
              {/* Solutions Dropdown.
                  top-full + pt-2: the 8px gap is PADDING INSIDE the hover area
                  rather than a margin outside it, so there is no dead strip
                  between the trigger and the menu for the cursor to cross. */}
              {showSolutionsDropdown && (
                <div className="absolute left-0 top-full pt-2 w-72 z-20">
                  <div className="bg-gray-900 border border-gray-800 rounded-md shadow-lg">
                  {/* The first three have dedicated /solutions/<x> marketing
                      pages; the rest link straight to the analysis page, since
                      no marketing page exists for them and a dead link would be
                      worse than sending someone to the thing that runs. */}
                  <div className="p-2">
                    <Link to="/analyze/encounter" className="block px-4 py-2 text-sm text-purple-300 hover:bg-gray-800 hover:text-purple-200 rounded-md font-medium">
                      Screening Encounter
                      <span className="ml-2 text-[10px] text-purple-400/80">multi-image</span>
                    </Link>
                    <div className="my-1 border-t border-gray-800" />
                    <Link to="/solutions/xray" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-purple-400 rounded-md">
                      X-Ray Analysis
                    </Link>
                    <Link to="/analyze/retina" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-purple-400 rounded-md">
                      Retinal Fundus Screening
                    </Link>
                    <Link to="/analyze/foot" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-purple-400 rounded-md">
                      Diabetic Foot Assessment
                    </Link>
                    <Link to="/solutions/skin" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-purple-400 rounded-md">
                      Skin Lesion Detection
                    </Link>
                    <Link to="/solutions/ct-scan" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-purple-400 rounded-md">
                      Breast Ultrasound
                    </Link>
                    <Link to="/analyze/fetal-plane" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-purple-400 rounded-md">
                      Fetal Ultrasound Plane
                    </Link>
                    <div className="my-1 border-t border-gray-800" />
                    <Link to="/solutions" className="block px-4 py-2 text-xs text-gray-500 hover:bg-gray-800 hover:text-gray-300 rounded-md">
                      All solutions
                    </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Direct route to the primary screening workflow. Previously the
                only way in was the Dashboard, which meant signing in first —
                awkward in a demo. */}
            <Link to="/analyze/encounter" className="text-gray-300 hover:text-purple-400 font-medium transition-colors">
              Screening
            </Link>

            <Link to="/how-it-works" className="text-gray-300 hover:text-purple-400 font-medium transition-colors">
              How It Works
            </Link>

            <Link to="/deployment" className="text-gray-300 hover:text-purple-400 font-medium transition-colors">
              Deployment
            </Link>
            
            <Link to="/partners" className="text-gray-300 hover:text-purple-400 font-medium transition-colors">
              Partners
            </Link>
            
            <Link to="/about" className="text-gray-300 hover:text-purple-400 font-medium transition-colors">
              About Us
            </Link>

            {currentUser?.role === 'superadmin' && (
              <Link to="/admin" className="text-gray-300 hover:text-purple-400 font-medium transition-colors">
                Admin
              </Link>
            )}

            <div className="flex items-center space-x-4">
              <Link to="/demo" className="btn btn-outline text-sm py-1.5 px-5 border border-purple-500 text-purple-400 hover:bg-purple-600/10 rounded-md transition-colors">
                Web Demo
              </Link>
              <Link to="/contact" className="btn btn-primary text-sm py-1.5 px-5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-md transition-colors">
                Contact Us
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-gray-300 hover:text-purple-400 hover:bg-gray-800 focus:outline-none"
            >
              {isOpen ? <IoClose size={24} /> : <GiHamburgerMenu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pt-4 pb-3 border-t border-gray-800">
            <div className="flex flex-col space-y-4">
              <div>
                <div
                  className="px-4 py-2 text-gray-300 hover:text-purple-400 hover:bg-gray-800 rounded flex justify-between items-center"
                  onClick={toggleSolutionsDropdown}
                >
                  Solutions
                  <svg className={`w-4 h-4 transition-transform ${showSolutionsDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                
                {showSolutionsDropdown && (
                  <div className="ml-4 mt-2 border-l border-gray-800 pl-4 space-y-2">
                    <Link
                      to="/solutions/xray"
                      className="block py-2 text-sm text-gray-400 hover:text-purple-400"
                      onClick={closeMobileMenu}
                    >
                      X-Ray Analysis
                    </Link>
                    <Link
                      to="/solutions/skin"
                      className="block py-2 text-sm text-gray-400 hover:text-purple-400"
                      onClick={closeMobileMenu}
                    >
                      Skin Lesion Detection
                    </Link>
                    <Link
                      to="/solutions/ct-scan"
                      className="block py-2 text-sm text-gray-400 hover:text-purple-400"
                      onClick={closeMobileMenu}
                    >
                      Ultrasound Interpretation
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/how-it-works"
                className="px-4 py-2 text-gray-300 hover:text-purple-400 hover:bg-gray-800 rounded"
                onClick={closeMobileMenu}
              >
                How It Works
              </Link>

              <Link
                to="/deployment"
                className="px-4 py-2 text-gray-300 hover:text-purple-400 hover:bg-gray-800 rounded"
                onClick={closeMobileMenu}
              >
                Deployment
              </Link>
              
              <Link
                to="/partners"
                className="px-4 py-2 text-gray-300 hover:text-purple-400 hover:bg-gray-800 rounded"
                onClick={closeMobileMenu}
              >
                Partners
              </Link>
              
              <Link
                to="/about"
                className="px-4 py-2 text-gray-300 hover:text-purple-400 hover:bg-gray-800 rounded"
                onClick={closeMobileMenu}
              >
                About Us
              </Link>

              {currentUser?.role === 'superadmin' && (
                <Link
                  to="/admin"
                  className="px-4 py-2 text-gray-300 hover:text-purple-400 hover:bg-gray-800 rounded"
                  onClick={closeMobileMenu}
                >
                  Admin
                </Link>
              )}

              <Link
                to="/demo"
                className="px-4 py-2 text-purple-400 hover:text-purple-300 hover:bg-gray-800 rounded border border-purple-500/50"
                onClick={closeMobileMenu}
              >
                Web Demo
              </Link>
              
              <Link
                to="/contact"
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 rounded"
                onClick={closeMobileMenu}
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;